import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';

import {
    CssBaseline,
    ThemeProvider,
    TSLTheme,
} from '@thirdspacelearning/library';
import { AuthProvider, MembershipType } from '@tsl-frontend/tsl-keycloak-auth';
import { MaintenanceProvider } from '@tsl-frontend/tsl-maintenance';
import { LaunchDarklyProvider } from '@tsl-frontend/tsl-launch-darkly';
import { queryClient } from '@tsl-frontend/tsl-utilities';

import App from './app/App';
import { store } from './store';

import { useLaunchDarklyContext } from '@hooks';
import { CurriculumSnackbar } from '@shared';
import { environment } from '@environments/environment';

const root = createRoot(document.getElementById('root')!);
root.render(
    <React.StrictMode>
        <Provider store={store}>
            <AuthProvider
                keycloakUrl={environment.keycloakUrl}
                keycloakRealm={environment.keycloakRealm}
                keycloakClientId={environment.keycloakClientId}
                allowedMemberships={[
                    MembershipType.ACADEMIC_COUNCILLOR,
                    MembershipType.CONTENT_CREATOR,
                ]}
            >
                <QueryClientProvider client={queryClient}>
                    <LaunchDarklyProvider
                        clientSideID={environment.launchDarklyKey}
                        useLaunchDarklyContext={useLaunchDarklyContext}
                    >
                        <ThemeProvider theme={TSLTheme}>
                            <CssBaseline />
                            <SnackbarProvider
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                Components={{
                                    curriculumSnackbar: CurriculumSnackbar,
                                }}
                                autoHideDuration={3000}
                                preventDuplicate={true}
                            />
                            <Router>
                                <MaintenanceProvider>
                                    <App />
                                </MaintenanceProvider>
                            </Router>
                        </ThemeProvider>
                    </LaunchDarklyProvider>
                </QueryClientProvider>
            </AuthProvider>
        </Provider>
    </React.StrictMode>,
);
